/**
 * Client-side usage-event batching (issue #1451, part of epic #1448).
 *
 * `trackEvent` is the small, synchronous, fire-and-forget helper the issue
 * asks for — route-change (`frontend/src/lib/routeUsageTracking.ts`) and
 * feature-click call sites just call it and move on. Events queue in memory
 * and flush together through the same `/platform-usage/events` batch
 * endpoint issue #1449 built (and issue #1450's `trackUsageEvent` already
 * posts to, one event at a time) — this module exists specifically to avoid
 * a network call per navigation/click, per the scope note in #1451.
 *
 * Flush triggers:
 *  - every `FLUSH_INTERVAL_MS` on a timer
 *  - immediately when the tab is hidden (`visibilitychange`)
 *  - immediately on `beforeunload`, via `fetch(..., { keepalive: true })` so
 *    the request can survive the page tearing down — `navigator.sendBeacon`
 *    cannot carry the `Authorization` header `apiRequest` needs, so it isn't
 *    an option here
 *  - eagerly, mid-interval, if the queue reaches the backend's per-batch cap
 *    (`platformUsageEventBatchSchema`'s `MAX_EVENTS_PER_BATCH`,
 *    `backend/src/validation/platformUsageSchemas.ts`)
 *
 * Best-effort throughout: a flush that fails is logged and the batch is
 * dropped, never retried into an unbounded queue and never thrown into a
 * caller — same fire-and-forget contract as `usageTracking.ts#trackUsageEvent`.
 */

import { apiRequest } from './apiClient/http';
import { getSessionId } from './usageSession';

interface QueuedUsageEvent {
  eventType: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

// Mirrors backend/src/validation/platformUsageSchemas.ts's MAX_EVENTS_PER_BATCH.
const MAX_EVENTS_PER_BATCH = 100;

const FLUSH_INTERVAL_MS = 15_000;

let queue: QueuedUsageEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let visibilityListener: (() => void) | null = null;
let beforeUnloadListener: (() => void) | null = null;

/**
 * Enqueues a usage event for the next batch flush. Never throws, never
 * blocks — call sites (route-change subscriptions, feature CTAs) fire this
 * and move on.
 */
export function trackEvent(name: string, meta?: Record<string, unknown>): void {
  queue.push({
    eventType: name,
    timestamp: new Date().toISOString(),
    ...(meta && Object.keys(meta).length > 0 ? { metadata: meta } : {}),
  });

  if (queue.length >= MAX_EVENTS_PER_BATCH) {
    void flushUsageEventQueue();
  }
}

async function postBatch(events: QueuedUsageEvent[], keepalive: boolean): Promise<void> {
  const sessionId = getSessionId();

  try {
    await apiRequest('/platform-usage/events', {
      method: 'POST',
      keepalive,
      headers: sessionId ? { 'X-Session-Id': sessionId } : {},
      body: JSON.stringify({ events }),
    });
  } catch (error) {
    // Best-effort — never propagate a tracking failure to the caller.
    console.error('flushUsageEventQueue: failed to flush usage event batch', error);
  }
}

/**
 * Flushes every currently-queued event. Chunks into batches no larger than
 * the backend's per-request cap; each chunk is posted independently so one
 * failing chunk doesn't drop the rest.
 */
export async function flushUsageEventQueue(options: { keepalive?: boolean } = {}): Promise<void> {
  if (queue.length === 0) return;

  const batch = queue;
  queue = [];

  const chunks: QueuedUsageEvent[][] = [];
  for (let i = 0; i < batch.length; i += MAX_EVENTS_PER_BATCH) {
    chunks.push(batch.slice(i, i + MAX_EVENTS_PER_BATCH));
  }

  await Promise.all(chunks.map((chunk) => postBatch(chunk, options.keepalive ?? false)));
}

/**
 * Starts the interval + unload-flush wiring. Idempotent — safe to call
 * multiple times (e.g. from a hot-reloaded entry module); only the first
 * call attaches listeners.
 */
export function startUsageEventBatching(): void {
  if (flushTimer) return;

  flushTimer = setInterval(() => {
    void flushUsageEventQueue();
  }, FLUSH_INTERVAL_MS);

  visibilityListener = (): void => {
    if (document.visibilityState === 'hidden') {
      void flushUsageEventQueue();
    }
  };
  beforeUnloadListener = (): void => {
    void flushUsageEventQueue({ keepalive: true });
  };

  document.addEventListener('visibilitychange', visibilityListener);
  window.addEventListener('beforeunload', beforeUnloadListener);
}

/**
 * Stops the interval + unload-flush wiring and drops any queued (unflushed)
 * events. Production code has no caller — the batching runs for the tab's
 * lifetime — this exists so unit tests can tear down cleanly between specs
 * instead of leaking timers/listeners onto the shared jsdom `document`.
 */
export function stopUsageEventBatching(): void {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  if (visibilityListener) {
    document.removeEventListener('visibilitychange', visibilityListener);
    visibilityListener = null;
  }
  if (beforeUnloadListener) {
    window.removeEventListener('beforeunload', beforeUnloadListener);
    beforeUnloadListener = null;
  }
  queue = [];
}
